/**
 * Primitivas visuales compartidas por las vistas de Recobros.
 *
 * Se extraen aquí (en vez de copiarlas en cada vista, como hace Impagos)
 * porque el panel tiene tres pantallas —listado, detalle y feed shadow— que
 * comparten tabla, badges y estados de carga/error/vacío. Los estilos son los
 * mismos tokens inline que usa Impagos, para que el panel no desentone.
 */

import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import {
  CADENCE_STATUS_LABEL,
  CADENCE_STATUS_STYLES,
} from "../../../types/msops";
import type { CadenceStatus, MsOpsMarket } from "../../../types/msops";

export const COLORS = {
  ink: "#1f2933",
  muted: "#73879C",
  heading: "#2A3F54",
  link: "#0069d9",
  border: "#e9ecef",
  rowBorder: "#f1f3f5",
  surface: "#ffffff",
  subtle: "#f8f9fb",
  dangerBg: "#fde2e2",
  dangerFg: "#b32424",
};

export function Th({
  children,
  style,
}: {
  children?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <th
      style={{
        padding: "0.55rem 0.75rem",
        textAlign: "left",
        fontSize: ".7rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: ".4px",
        color: COLORS.muted,
        borderBottom: `1px solid ${COLORS.border}`,
        ...style,
      }}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  mono,
  style,
}: {
  children?: ReactNode;
  mono?: boolean;
  style?: CSSProperties;
}) {
  return (
    <td
      style={{
        padding: "0.55rem 0.75rem",
        fontFamily: mono ? "monospace" : "inherit",
        color: COLORS.ink,
        verticalAlign: "middle",
        ...style,
      }}
    >
      {children}
    </td>
  );
}

export function Badge({
  bg,
  fg,
  title,
  children,
}: {
  bg: string;
  fg: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      style={{
        display: "inline-block",
        padding: "0.15rem 0.5rem",
        borderRadius: "999px",
        background: bg,
        color: fg,
        fontSize: ".7rem",
        fontWeight: 700,
        letterSpacing: ".3px",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

export function Card({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        background: COLORS.surface,
        borderRadius: "0.6rem",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        overflow: "hidden",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: "0.6rem 0.85rem",
        background: COLORS.dangerBg,
        color: COLORS.dangerFg,
        borderRadius: "0.45rem",
        marginBottom: "0.65rem",
        fontSize: ".82rem",
      }}
    >
      {message}
    </div>
  );
}

export function Loading({ label }: { label: string }) {
  return <div style={{ padding: "1.5rem", color: COLORS.muted }}>{label}</div>;
}

export function EmptyRow({
  colSpan,
  children,
}: {
  colSpan: number;
  children: ReactNode;
}) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        style={{ padding: "1rem", color: COLORS.muted, textAlign: "center" }}
      >
        {children}
      </td>
    </tr>
  );
}

export const inputStyle: CSSProperties = {
  padding: "0.4rem 0.55rem",
  borderRadius: "0.4rem",
  border: "1px solid #dee2e6",
  background: COLORS.subtle,
  fontSize: ".85rem",
};

export const labelStyle: CSSProperties = {
  fontSize: ".72rem",
  color: COLORS.muted,
  fontWeight: 600,
};

export const fieldStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.2rem",
};

export const buttonStyle: CSSProperties = {
  padding: "0.4rem 0.8rem",
  borderRadius: "0.4rem",
  background: COLORS.link,
  color: "white",
  border: `1px solid ${COLORS.link}`,
  fontSize: ".8rem",
  fontWeight: 600,
  cursor: "pointer",
};

export const secondaryButtonStyle: CSSProperties = {
  ...buttonStyle,
  background: "white",
  color: COLORS.muted,
  borderColor: "#dee2e6",
};

/** Fecha + hora — en el panel casi todo es un instante, no un día. */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/**
 * Aviso mostrado cuando el passthrough devuelve 404: el panel existe en el
 * bundle pero ms-ops no está desplegado/configurado en este entorno.
 */
export const MSOPS_UNAVAILABLE_MESSAGE =
  "Recobros no está disponible en este entorno (ms-ops no configurado).";

/**
 * Traduce un fallo de `HttpError` a algo que Ops pueda accionar.
 *
 * Ojo con los dos códigos "especiales" del passthrough `/api/admin/ops/*`:
 *   404 → el admin-api no tiene `MS_OPS_BASE_URL` en este entorno; no es un
 *         error del operador sino el feature flag apagado.
 *   403 → el empleado no tiene permiso para esta sección. El 401 ya NO se
 *         trata aquí: al ir por el admin-api es una sesión caducada normal y
 *         lo gestiona el handler global de `api/http.ts` (logout + /login).
 */
export function describeError(err: unknown, fallback: string): string {
  const status = (err as { status?: number } | null)?.status;
  if (status === 404) {
    return MSOPS_UNAVAILABLE_MESSAGE;
  }
  if (status === 403) {
    return "Sin permisos para consultar Recobros (revisa el rol del usuario).";
  }
  if (typeof status === "number") {
    return `${fallback} (HTTP ${status})`;
  }
  return fallback;
}

export function UnavailableNotice() {
  return (
    <div style={{ padding: "1.5rem" }}>
      <Card style={{ padding: "1.1rem 1.25rem", maxWidth: 620 }}>
        <h2 style={{ margin: 0, fontSize: "1.05rem", color: COLORS.heading }}>
          Recobros no disponible
        </h2>
        <p
          style={{
            margin: "0.5rem 0 0",
            color: COLORS.muted,
            fontSize: ".85rem",
            lineHeight: 1.5,
          }}
        >
          El motor de cadencias (ms-ops) no está configurado en este entorno,
          así que el admin-api no expone <code>/api/admin/ops/*</code>. La
          sección se oculta del menú automáticamente.
        </p>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estado derivado de una cadencia (ms-ops §F3.b).
//
// El panel pintaba "Activa" en verde para toda cadencia con `paused: false`.
// Era falso: el envío depende ADEMÁS del gate por mercado de `app_settings`,
// y con todos los gates cerrados el motor está en shadow y no manda nada. Un
// operador podía enseñar el panel y creer que había comunicaciones saliendo.
//
// Estas primitivas pintan el estado DERIVADO que ya calcula el backend, con su
// motivo en castellano siempre visible: el badge dice el qué, el motivo el
// porqué, y ninguno de los dos se puede leer como lo contrario de lo que pasa.
// ---------------------------------------------------------------------------

/**
 * Forma mínima que necesita la UI de estado. Se declara estructural (y no como
 * `CadenceListItem`) para que valga igual con el item del listado, el detalle
 * o un snapshot: lo único que se pinta son estos cuatro campos.
 */
export type CadenceStatusFields = {
  status: CadenceStatus;
  statusReason: string;
  marketsActive: MsOpsMarket[];
  marketsShadow: MsOpsMarket[];
};

/** Badge del estado derivado. El `title` repite el motivo para el hover. */
export function CadenceStatusBadge({ status, reason }: {
  status: CadenceStatus;
  reason?: string;
}) {
  const style = CADENCE_STATUS_STYLES[status];
  return (
    <Badge bg={style.bg} fg={style.fg} title={reason}>
      {CADENCE_STATUS_LABEL[status]}
    </Badge>
  );
}

/**
 * Badge + motivo, la unidad que se usa en el listado y en la cabecera del
 * editor. El motivo NUNCA es opcional: sin él, "Sin efecto" o "Modo shadow" no
 * dicen por qué, que es justo la pregunta que Ops se hace a continuación.
 *
 * `markets` añade el desglose de qué mercados envían de verdad — con el gate
 * escalonado, "activa" puede significar sólo ES mientras FR e IT siguen en
 * shadow, y esa diferencia es la que decide si hay que actuar.
 */
export function CadenceStatusCell({
  cadence,
  markets = false,
}: {
  cadence: CadenceStatusFields;
  markets?: boolean;
}) {
  return (
    <div>
      <CadenceStatusBadge
        status={cadence.status}
        reason={cadence.statusReason}
      />
      <div
        style={{
          marginTop: "0.25rem",
          fontSize: ".72rem",
          color: COLORS.muted,
          lineHeight: 1.4,
          maxWidth: 320,
        }}
      >
        {cadence.statusReason}
      </div>
      {markets && cadence.marketsActive.length > 0 && (
        <div
          style={{
            marginTop: "0.25rem",
            fontSize: ".72rem",
            color: COLORS.dangerFg,
            fontWeight: 600,
          }}
        >
          Enviando en: {cadence.marketsActive.join(" · ")}
        </div>
      )}
    </div>
  );
}

/**
 * Desglose de gates por mercado, para el detalle del editor.
 *
 * Es la traducción visual de `marketsActive`/`marketsShadow`: los mercados que
 * la cadencia declara (unión de los `templateByMarket` de sus pasos), cada uno
 * con si su gate está abierto o cerrado. Un mercado que no aparece aquí es un
 * mercado que la cadencia no sabe atender, y decirlo evita la confusión de
 * buscar un gate que no existe.
 */
export function CadenceMarketGates({ cadence }: { cadence: CadenceStatusFields }) {
  const total = cadence.marketsActive.length + cadence.marketsShadow.length;
  if (total === 0) {
    return (
      <div style={{ fontSize: ".8rem", color: COLORS.muted }}>
        Esta cadencia no declara plantilla para ningún mercado, así que el motor
        salta todos sus pasos de comunicación.
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
      {cadence.marketsActive.map((market) => (
        <Badge
          key={market}
          bg="#fde2e2"
          fg="#b32424"
          title="Gate abierto: el motor ENVÍA a los clientes de este mercado"
        >
          {market}: enviando
        </Badge>
      ))}
      {cadence.marketsShadow.map((market) => (
        <Badge
          key={market}
          bg="#e7f1ff"
          fg="#0069d9"
          title="Gate cerrado: el motor decide y registra, pero no envía"
        >
          {market}: shadow
        </Badge>
      ))}
    </div>
  );
}

/**
 * Aviso global de "aquí no sale nada".
 *
 * Se pinta cuando NINGUNA cadencia tiene un mercado con el gate abierto. Es
 * deliberadamente discreto (azul, no rojo: el modo shadow es el estado
 * esperado del rollout, no una avería) pero está arriba del todo, porque el
 * momento en que más engaña el panel es cuando alguien lo enseña por primera
 * vez y da por hecho que lo que ve es tráfico real.
 */
export function ShadowModeBanner() {
  return (
    <div
      style={{
        padding: "0.6rem 0.85rem",
        background: "#e7f1ff",
        color: "#0b4f9e",
        border: "1px solid #b8d8ff",
        borderRadius: "0.45rem",
        marginBottom: "0.65rem",
        fontSize: ".82rem",
        lineHeight: 1.5,
      }}
    >
      <strong>El motor está en modo shadow:</strong> registra decisiones pero no
      envía comunicaciones a clientes. Ninguna cadencia tiene el gate de mercado
      abierto, así que todo lo que ves aquí es lo que el motor <em>habría</em>{" "}
      hecho.
    </div>
  );
}

/**
 * Valor de sólo lectura con botón de copiar — el gesto real de Ops con los
 * links de pago y el OTP es *copiar y pegar* en un WhatsApp o un email, así
 * que el botón está donde está el valor y no en un menú.
 *
 * Usa `navigator.clipboard` cuando existe y cae a `document.execCommand` en
 * los contextos donde no (el SPA se sirve por HTTPS, pero un iframe sin
 * permiso de portapapeles rompería el único gesto que importa aquí).
 */
export function CopyableValue({
  value,
  label,
  mono = true,
}: {
  value: string;
  label?: string;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const area = document.createElement("textarea");
        area.value = value;
        area.style.position = "fixed";
        area.style.opacity = "0";
        document.body.appendChild(area);
        area.select();
        document.execCommand("copy");
        document.body.removeChild(area);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Si el navegador lo bloquea, el valor sigue visible y seleccionable a
      // mano: no merece un banner de error.
    }
  };

  return (
    <div>
      {label && <div style={labelStyle}>{label}</div>}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.4rem",
        }}
      >
        <input
          readOnly
          value={value}
          onFocus={(e) => e.currentTarget.select()}
          style={{
            ...inputStyle,
            flex: 1,
            minWidth: 0,
            fontFamily: mono ? "monospace" : "inherit",
            fontSize: ".78rem",
          }}
        />
        <button
          type="button"
          onClick={copy}
          style={{
            ...secondaryButtonStyle,
            whiteSpace: "nowrap",
            color: copied ? "#155724" : COLORS.muted,
            borderColor: copied ? "#155724" : "#dee2e6",
          }}
        >
          {copied ? "Copiado" : "Copiar"}
        </button>
      </div>
    </div>
  );
}
